import { createTenantBootstrapService } from "./createTenantBootstrapService";
import { createTenantFirebaseInitializer } from "./createTenantFirebaseInitializer";
import { resolveTenantSlugFromPath } from "./tenantSlug";
import {
  clearTenantScopedState,
  hasProvisionedWorkspace,
  readActiveTenantSlug,
  readCachedTenantBootstrap,
  writeActiveTenantSlug,
  writeCachedTenantBootstrap,
} from "./tenantBootstrapStorage";
import { TENANT_BOOTSTRAP_ERROR_CODES } from "./bootstrapErrors";
import { createCompanyLogoCache } from "./companyLogoCache";

export const createTenantBootstrapRuntime = ({
  tenantBootstrapService,
  tenantFirebaseInitializer,
  readCachedBootstrap = readCachedTenantBootstrap,
  writeCachedBootstrap = writeCachedTenantBootstrap,
  readCurrentActiveTenantSlug = readActiveTenantSlug,
  writeCurrentActiveTenantSlug = writeActiveTenantSlug,
  clearCurrentTenantState = clearTenantScopedState,
  hasLocalProvisionedWorkspace = hasProvisionedWorkspace,
  isOnline = () => window.navigator.onLine,
  companyLogoCache = createCompanyLogoCache(),
} = {}) => {
  let currentTenantBootstrap = null;
  let currentTenantFirebase = null;
  const resolvedTenantBootstrapService =
    tenantBootstrapService || createTenantBootstrapService();
  const resolvedTenantFirebaseInitializer =
    tenantFirebaseInitializer || createTenantFirebaseInitializer();

  return {
    getTenantSlugFromPath(pathname) {
      try {
        return resolveTenantSlugFromPath(pathname);
      } catch {
        return "";
      }
    },

    getCurrentTenantBootstrap() {
      return currentTenantBootstrap;
    },

    getCurrentTenantFirebase() {
      return currentTenantFirebase;
    },

    getRequiredCurrentTenantFirebase() {
      if (!currentTenantFirebase) {
        throw new Error("Customer Firebase is not initialized yet.");
      }

      return currentTenantFirebase;
    },

    reset() {
      currentTenantBootstrap = null;
      currentTenantFirebase = null;
    },

    async bootstrapForPathname(pathname) {
      const tenantSlug = resolveTenantSlugFromPath(pathname);
      const previousTenantSlug = readCurrentActiveTenantSlug();

      if (previousTenantSlug && previousTenantSlug !== tenantSlug) {
        clearCurrentTenantState();
        this.reset();
      }

      console.info(`[Tenant bootstrap] resolving slug=${tenantSlug}`);

      const cachedTenantBootstrap = readCachedBootstrap(tenantSlug);
      const canUseOfflineCache =
        Boolean(cachedTenantBootstrap) && hasLocalProvisionedWorkspace();

      let tenantBootstrap = null;
      let source = "central";

      if (isOnline()) {
        tenantBootstrap = await resolvedTenantBootstrapService.resolveTenant(
          tenantSlug
        );
        tenantBootstrap = await companyLogoCache.cache(tenantBootstrap);
        writeCachedBootstrap(tenantBootstrap);
      } else if (canUseOfflineCache) {
        tenantBootstrap = cachedTenantBootstrap;
        source = "cache";
      } else {
        const error = new Error("Central Firestore is unavailable.");
        error.code = TENANT_BOOTSTRAP_ERROR_CODES.CENTRAL_FIRESTORE_UNAVAILABLE;
        throw error;
      }

      console.info("[Tenant bootstrap] tenant resolved", {
        tenantSlug,
        source,
        compatibilityStatusDefaulted:
          tenantBootstrap.compatibilityStatusDefaulted || false,
      });

      console.info(
        `[Tenant bootstrap] initializing customer Firebase project=${tenantBootstrap.firebaseConfig.projectId}`
      );

      const tenantFirebase =
        resolvedTenantFirebaseInitializer.initializeTenantFirebase(
          tenantBootstrap.firebaseConfig,
          tenantSlug
        );

      currentTenantBootstrap = tenantBootstrap;
      currentTenantFirebase = tenantFirebase;
      writeCurrentActiveTenantSlug(tenantSlug);

      return {
        tenantBootstrap,
        tenantFirebase,
        source,
      };
    },
  };
};
